// Copyright © 2021 Move Closer

import { Component, Prop, Vue } from 'vue-property-decorator'

import { BootstrapImage, BootstrapLink } from '../../../dsl/front'
import { Author, ImageRatio } from '../../_contracts'
import { PublicationDate } from '../PublicationDate'
import { ReadingTime } from '../ReadingTime'
import { prepareImage } from '../../_filters'

/**
 * @author Sebastian Dziechciarz <sebastian.dziechciarz@movecloser.pl>
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Michał Rossian <michal.rossian@movecloser.pl>
 */
@Component<ArticleAuthor>({
  name: 'ArticleAuthor',
  components: { BootstrapImage, BootstrapLink, PublicationDate, ReadingTime },

  template: `
    <div class="article-author">
      <component
        v-if="showAuthorAvatar || showInitialsWhenNoAvatar || showAuthorName"
        :is="Component"
        v-bind="link ? { link : { target: link } } : {}"
        class="article-author__wrap"
      >
        <BootstrapImage v-if="showAuthorAvatar && avatar && avatar.src && avatar.src.length"
                        v-bind="avatar" class="article-author__avatar img-avatar" />

        <div v-else-if="showInitialsWhenNoAvatar" class="article-author__initials">
          {{ name.split(' ')[0][0] }}{{ name.split(' ')[1][0] }}
        </div>

        <span v-if="showAuthorName && name && name.length" class="article-author__name">
          {{ name }}
        </span>
      </component>

      <div class="article-author__texts">
        <template v-if="!hideDate">
          <PublicationDate
            v-if="showPublicationDate"
            :value="publicationDate"
            :shortenDate="shortenDate || isMobile"
          />

          <!-- <ReadingTime v-if="showReadingTime" :time="999" /> -->
        </template>

        <slot />
      </div>
    </div>
  `
})
export class ArticleAuthor extends Vue {
  @Prop({ type: Object, required: true })
  public readonly author!: Author

  /**
   * Determine whether pub.date should be hidden
   */
  @Prop({ type: Boolean, required: false, default: false })
  public readonly hideDate!: boolean

  @Prop({ type: Boolean, required: false, default: false })
  public readonly isMobile!: boolean

  @Prop({ type: Boolean, required: false, default: false })
  protected readonly isLazy!: boolean

  /**
   * The date when the article has been published.
   */
  @Prop({ type: String, required: false, default: null })
  private readonly publicationDate!: string | null

  /**
   * Determinate if there should be shorten date
   */
  @Prop({ type: Boolean, required: false, default: false })
  private readonly shortenDate!: boolean

  /**
   * Determines whether the author's avatar should be shown.
   */
  @Prop({ type: Boolean, required: false, default: true })
  private readonly showAuthorAvatar!: boolean

  /**
   * Determines whether the author's name should be shown.
   */
  @Prop({ type: Boolean, required: false, default: true })
  private readonly showAuthorName!: boolean

  @Prop({ type: Boolean, required: false, default: false })
  public readonly showInitialsWhenNoAvatar!: boolean

  /**
   * Determines whether the publication date should be shown.
   */
  @Prop({ type: Boolean, required: false, default: true })
  private readonly showPublicationDate!: boolean

  @Prop({ type: Boolean, required: false, default: false })
  private readonly showReadingTime!: boolean

  public get Component () {
    return this.link ? BootstrapLink : 'div'
  }

  public get avatar () {
    if (!this.author?.avatar) {
      return
    }

    return prepareImage(this.author.avatar, ImageRatio.Square, 64, this.isLazy)
  }

  public get name () {
    if (!this.author) {
      return ''
    }

    return `${this.author.firstName} ${this.author.lastName}`
  }

  public get link () {
    if (!this.author?.url) {
      return ''
    }

    return this.author.url
  }
}
